import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEventForm } from '../../../hooks/useEventForms';
import { useEventContext } from '../../contexts/EventContext';
import { useAccount } from '../../../hooks/useAccount';
import { FormProgressBar } from '../../../public_home/components/forms/FormProgressBar';
import { FormNavigation } from '../../../public_home/components/forms/FormNavigation';
import { FormPage } from '../../../public_home/components/forms/FormPage';
import { FormConfirmation } from '../../../public_home/components/forms/FormConfirmation';
import { EventFormWelcome } from './EventFormWelcome';
import { FormBuiltinFields } from '../../../public_home/components/forms/FormBuiltinFields';
import { AutosaveIndicator } from '../../../public_home/components/forms/AutosaveIndicator';
import { FormContext } from '../../../public_home/components/forms/FormContext';
import { AuthenticationRequired } from '../../../components/common/AuthenticationRequired';
import { FormErrorBanner } from './FormErrorBanner';
import { useEventFormDraft } from '../../../hooks/useEventFormDraft';
import { useEventFormValidation } from '../../../hooks/useEventFormValidation';
import { useEventFormPage, useSubmitEventForm } from '../../../hooks/useEventFormOperations';
import type { FormState, Question, FormResponses } from '../../../types/publicForm.types';

export const EventFormPublic: React.FC = () => {
  const { form_slug } = useParams<{ form_slug: string }>();
  const { eventSlug } = useEventContext();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: account, refetch: refetchAccount } = useAccount();

  // Log component mount/unmount
  useEffect(() => {
    console.log('🚀 EventFormPublic MOUNTED');
    return () => {
      console.log('💥 EventFormPublic UNMOUNTED');
    };
  }, []);

  // Fetch form data using event context
  const { data: form, isLoading: isFormLoading, error: formError } = useEventForm(eventSlug, form_slug!);

  // Form state
  const [formState, setFormState] = useState<FormState>({
    currentPageIndex: 0,
    formData: {},
    touchedFields: new Set(),
    visibleChildQuestions: new Set(),
    validationErrors: {},
    isSubmitting: false,
    showConfirmation: false,
  });
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Welcome page state
  const urlParams = new URLSearchParams(location.search);
  const isReturningFromAuth = urlParams.get('authenticated') === 'true';
  const shouldSkipWelcome = location.pathname.endsWith('/new') || isReturningFromAuth;
  const [showWelcomePage, setShowWelcomePage] = useState(!shouldSkipWelcome);
  const [hasStartedForm, setHasStartedForm] = useState(shouldSkipWelcome);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [builtinFieldsCompleted, setBuiltinFieldsCompleted] = useState(false);
  const [draftDataLoaded, setDraftDataLoaded] = useState(false);

  // Check authentication requirements when form loads
  useEffect(() => {
    if (form) {
      setRequiresAuth(form.requiresAccount || false);
      setIsAuthenticated(!!account);
    }
  }, [form, account]);

  // Refetch account when returning from authentication
  useEffect(() => {
    if (isReturningFromAuth) {
      refetchAccount().then(() => {
        // Remove the authenticated param from URL after handling
        const newParams = new URLSearchParams(location.search);
        newParams.delete('authenticated');
        const newSearch = newParams.toString();
        const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, '', newUrl);
      });
    }
  }, [isReturningFromAuth, refetchAccount, location]);

  // Draft management - use event-specific draft hook
  // Enable draft as soon as we have a valid form, not just when hasStartedForm is true
  // This ensures draft is ready when user starts filling out fields in FormBuiltinFields
  const draftFormSlug = form?.slug || form_slug || '';
  console.log('🔍 EventFormPublic Draft Hook Setup:', {
    eventSlug,
    formSlug: draftFormSlug,
    hasForm: !!form,
    formSlugParam: form_slug,
    enabled: !!draftFormSlug,
    hasStartedForm
  });
  
  const {
    draft,
    isDraftLoading,
    isSaving,
    lastSaved,
    saveError,
    saveChanges,
    saveNow,
    clearDraft
  } = useEventFormDraft({
    eventSlug,
    formSlug: draftFormSlug,
    enabled: !!draftFormSlug // Enable as soon as we have a form slug
  });

  // Log draft state changes
  useEffect(() => {
    console.log('📝 EventFormPublic Draft State:', {
      hasDraft: !!draft,
      authToken: draft?.authToken,
      isDraftLoading,
      isSaving,
      lastSaved,
      saveError
    });
  }, [draft, isDraftLoading, isSaving, lastSaved, saveError]);

  // Initialize form data from draft when it loads
  useEffect(() => {
    if (draft && hasStartedForm && !draftDataLoaded) {
      const initialFormData: Record<string, any> = {};

      // Load user data from draft
      if (draft.submission) {
        if (draft.submission.firstName) initialFormData.firstName = draft.submission.firstName;
        if (draft.submission.lastName) initialFormData.lastName = draft.submission.lastName;
        if (draft.submission.email) initialFormData.email = draft.submission.email;
        if (draft.submission.company) initialFormData.company = draft.submission.company;
        if (draft.submission.phone) initialFormData.phone = draft.submission.phone;

        // Load address data
        if (draft.submission.addressAttributes) {
          const addr = draft.submission.addressAttributes;
          if (addr.name) initialFormData.name = addr.name;
          if (addr.address1) initialFormData.address1 = addr.address1;
          if (addr.address2) initialFormData.address2 = addr.address2;
          if (addr.city) initialFormData.city = addr.city;
          if (addr.state) initialFormData.state = addr.state;
          if (addr.zip) initialFormData.zip = addr.zip;
          if (addr.country) initialFormData.country = addr.country;
        }
      }

      // Load question responses
      if (draft.responses) {
        Object.entries(draft.responses).forEach(([questionId, response]) => {
          if (response.value !== undefined) {
            initialFormData[questionId] = response.value;
          } else if (response.answerId) {
            initialFormData[questionId] = response.answerId;
          } else if (response.answerIds) {
            initialFormData[questionId] = response.answerIds;
          }

          if (response.comment) {
            initialFormData[`${questionId}_comment`] = response.comment;
          }
        });
      }

      setFormState(prev => ({
        ...prev,
        formData: initialFormData
      }));
      setDraftDataLoaded(true);
    }
  }, [draft, hasStartedForm, draftDataLoaded]);

  // Page validation - use event-specific validation hook
  const { validatePage, validatePageWithServer } = useEventFormValidation(
    eventSlug,
    form?.slug || form_slug || '',
    formState
  );

  // Fetch current page data - use event-specific page hook
  const { data: pageData, isLoading: isPageLoading } = useEventFormPage(
    eventSlug,
    form?.slug || form_slug || '',
    formState.currentPageIndex
  );

  // Form submission mutation - use event-specific submit hook
  const submitMutation = useSubmitEventForm();

  // Store event context when form loads
  useEffect(() => {
    if (form && form.event?.id) {
      sessionStorage.setItem('currentEventContext', JSON.stringify({
        eventId: form.event.id,
        eventSlug: eventSlug
      }));
    }
  }, [form, eventSlug]);

  // Helper to get a question by ID from current page data (including conditional questions)
  const getQuestionById = useCallback((questionId: string): Question | null => {
    if (!pageData?.page) return null;

    // Recursive function to search through questions and their conditional children
    const searchQuestion = (question: Question): Question | null => {
      if (question.id === questionId) {
        return question;
      }
      
      // Search through conditional questions (childQuestions in answers)
      if (question.answers) {
        for (const answer of question.answers) {
          if (answer.childQuestions) {
            for (const childQuestion of answer.childQuestions) {
              const found = searchQuestion(childQuestion);
              if (found) return found;
            }
          }
        }
      }
      
      return null;
    };

    // Search through all questions in all sections
    for (const section of pageData.page.surveySections) {
      for (const question of section.questions) {
        const found = searchQuestion(question);
        if (found) return found;
      }
    }
    return null;
  }, [pageData]);

  // Form actions
  const updateField = useCallback((questionId: string, value: any, batchedAddressUpdate?: Record<string, any>) => {
    console.log('🔄 EventFormPublic updateField called:', {
      questionId,
      value: typeof value === 'string' ? value.substring(0, 20) + '...' : value,
      hasBatchedUpdate: !!batchedAddressUpdate,
      hasDraft: !!draft,
      hasForm: !!form,
      draftAuthToken: draft?.authToken
    });

    setFormState(prev => {
      const newValidationErrors = { ...prev.validationErrors };
      const hasValidValue = value !== '' && value !== null && value !== undefined &&
                           (Array.isArray(value) ? value.length > 0 : true);

      if (hasValidValue && newValidationErrors[questionId]) {
        delete newValidationErrors[questionId];
      }

      // If this is part of a batched address update, update all address fields at once
      if (batchedAddressUpdate) {
        return {
          ...prev,
          formData: {
            ...prev.formData,
            ...batchedAddressUpdate,
          },
          validationErrors: newValidationErrors,
        };
      }

      return {
        ...prev,
        formData: {
          ...prev.formData,
          [questionId]: value,
        },
        validationErrors: newValidationErrors,
      };
    });

    // Autosave changes
    console.log('🔍 Before autosave check:', {
      hasDraft: !!draft,
      hasForm: !!form,
      willSave: !!(draft && form)
    });
    
    if (draft && form) {
      console.log('✅ Preparing to save changes for field:', questionId);
      const updates: any = {
        submission: draft.submission || {},
        responses: draft.responses || {}
      };
      const userFields = ['firstName', 'lastName', 'email', 'company', 'phone'];
      const addressFields = ['name', 'address1', 'address2', 'city', 'state', 'zip', 'country'];

      if (userFields.includes(questionId)) {
        updates.submission[questionId] = value;
      } else if (addressFields.includes(questionId)) {
        // If we have a batched address update, use those values directly
        if (batchedAddressUpdate) {
          updates.submission.addressAttributes = {
            name: batchedAddressUpdate.name || '',
            address_1: batchedAddressUpdate.address1 || '',
            address_2: batchedAddressUpdate.address2 || '',
            city: batchedAddressUpdate.city || '',
            state: batchedAddressUpdate.state || '',
            zip: batchedAddressUpdate.zip || '',
            country: batchedAddressUpdate.country || 'US'
          };
        } else {
          // Single address field update - still use all fields together
          updates.submission.addressAttributes = {
            name: questionId === 'name' ? value : (formState.formData.name || ''),
            address_1: questionId === 'address1' ? value : (formState.formData.address1 || ''),
            address_2: questionId === 'address2' ? value : (formState.formData.address2 || ''),
            city: questionId === 'city' ? value : (formState.formData.city || ''),
            state: questionId === 'state' ? value : (formState.formData.state || ''),
            zip: questionId === 'zip' ? value : (formState.formData.zip || ''),
            country: questionId === 'country' ? value : (formState.formData.country || 'US')
          };
        }
      } else {
        // Handle question responses
        if (questionId.endsWith('_comment')) {
          const baseQuestionId = questionId.replace('_comment', '');
          if (!updates.responses[baseQuestionId]) {
            updates.responses[baseQuestionId] = draft.responses?.[baseQuestionId] || {};
          }
          updates.responses[baseQuestionId].comment = value;
        } else {
          const question = getQuestionById(questionId);
          if (question) {
            // Handle multi-answer questions (checkboxes, multi-select dropdowns, multi-select)
            if ((question.answerStyle === 'checkboxes' || question.answerStyle === 'checkbox' ||
                 (question.answerStyle === 'dropdown' && question.allowMultipleAnswers) ||
                 (question.answerStyle === 'select' && question.allowMultipleAnswers)) &&
                Array.isArray(value)) {
              updates.responses[questionId] = { answerIds: value };
            }
            // Handle single-answer questions (radio, single-select dropdown, select)
            else if ((question.answerStyle === 'dropdown' || question.answerStyle === 'select' || question.answerStyle === 'radio' || question.answerStyle === 'multiple_choice') &&
                     !question.allowMultipleAnswers && value) {
              updates.responses[questionId] = { answerId: value };
            }
            // Handle tag questions
            else if (question.answerStyle === 'tag_question' && typeof value === 'object' && value !== null &&
                     ('tagIds' in value || 'tagRequests' in value || 'questionResponseTaggings' in value)) {
              // Tag question - save the complex data structure directly
              updates.responses[questionId] = value;
            }
            // Handle all other question types (text, textarea, etc.)
            else {
              updates.responses[questionId] = { value };
            }
          } else {
            // If we can't find the question, check for special formats
            if (Array.isArray(value)) {
              updates.responses[questionId] = { answerIds: value };
            } else if (typeof value === 'object' && value !== null &&
                       ('tagIds' in value || 'tagRequests' in value || 'questionResponseTaggings' in value)) {
              // Tag question data structure
              updates.responses[questionId] = value;
            } else {
              updates.responses[questionId] = { value };
            }
          }
        }
      }

      console.log('📤 Calling saveChanges with updates:', updates);
      saveChanges(updates);
    } else {
      console.log('❌ Not saving - missing draft or form:', {
        hasDraft: !!draft,
        hasForm: !!form
      });
    }
  }, [draft, form, saveChanges, getQuestionById, formState]);

  const markFieldTouched = useCallback((questionId: string) => {
    setFormState(prev => ({
      ...prev,
      touchedFields: new Set(prev.touchedFields).add(questionId),
    }));
  }, []);

  const setVisibleChildQuestions = useCallback((visibleSet: Set<string>) => {
    setFormState(prev => ({
      ...prev,
      visibleChildQuestions: visibleSet,
    }));
  }, []);

  const nextPage = useCallback(async () => {
    if (!pageData?.page) return;

    const currentPageQuestions: Question[] = [];
    pageData.page.surveySections.forEach(section => {
      currentPageQuestions.push(...section.questions);
    });

    const newTouchedFields = new Set(formState.touchedFields);
    currentPageQuestions.forEach(q => {
      newTouchedFields.add(q.id);
      if (q.showComments) {
        newTouchedFields.add(`${q.id}_comment`);
      }
    });

    setFormState(prev => ({
      ...prev,
      touchedFields: newTouchedFields,
    }));

    const validation = validatePage(currentPageQuestions);

    if (!validation.isValid) {
      setFormState(prev => ({
        ...prev,
        validationErrors: validation.errors,
      }));

      setTimeout(() => {
        const errorSummary = document.getElementById('validation-errors-summary');
        if (errorSummary && Object.keys(validation.errors).length > 0) {
          const yOffset = -100;
          const y = errorSummary.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);

      return;
    }

    if (draft) {
      try {
        await saveNow();
      } catch (error) {
        console.error('Failed to save draft before navigation:', error);
        // Still allow navigation even if save fails
      }
    }

    if (pageData?.survey?.totalPages && formState.currentPageIndex < pageData.survey.totalPages - 1) {
      window.scrollTo(0, 0);
      setFormState(prev => ({
        ...prev,
        currentPageIndex: prev.currentPageIndex + 1,
        validationErrors: {},
      }));
    }
  }, [pageData, formState.currentPageIndex, formState.touchedFields, validatePage, draft, saveNow]);

  const previousPage = useCallback(() => {
    if (formState.currentPageIndex > 0) {
      window.scrollTo(0, 0);
      setFormState(prev => ({
        ...prev,
        currentPageIndex: prev.currentPageIndex - 1,
      }));
    }
  }, [formState.currentPageIndex]);

  const submitForm = useCallback(async () => {
    if (!form || !draft) return;

    let hasErrors = false;
    const allErrors: Record<string, string> = {};

    if (pageData?.page) {
      const currentPageQuestions: Question[] = [];
      pageData.page.surveySections.forEach(section => {
        currentPageQuestions.push(...section.questions);
      });

      const validation = validatePage(currentPageQuestions);
      if (!validation.isValid) {
        hasErrors = true;
        Object.assign(allErrors, validation.errors);
      }
    }

    if (hasErrors) {
      setFormState(prev => ({
        ...prev,
        validationErrors: allErrors,
      }));

      setTimeout(() => {
        const errorSummary = document.getElementById('validation-errors-summary');
        if (errorSummary) {
          const yOffset = -100;
          const y = errorSummary.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);

      return;
    }

    setFormState(prev => ({ ...prev, isSubmitting: true }));
    setSubmissionError(null); // Clear any previous errors

    try {
      await saveNow();
    } catch (saveError: any) {
      console.error('Failed to save draft before submission:', saveError);
      // Don't prevent submission if draft save fails, but log it
      // The actual submission might still succeed
    }

    try {
      const submissionData: FormResponses = {
        submission: {
          firstName: formState.formData.firstName as string,
          lastName: formState.formData.lastName as string,
          email: formState.formData.email as string,
          company: formState.formData.company as string,
          phone: formState.formData.phone as string,
        },
        responses: {}
      };

      if (formState.formData.address1 || formState.formData.name) {
        submissionData.submission.addressAttributes = {
          name: formState.formData.name as string || '',
          address_1: formState.formData.address1 as string || '',
          address_2: formState.formData.address2 as string || '',
          city: formState.formData.city as string || '',
          state: formState.formData.state as string || '',
          zip: formState.formData.zip as string || '',
          country: formState.formData.country as string || 'US',
        };
      }

      Object.entries(formState.formData).forEach(([key, value]) => {
        if (['firstName', 'lastName', 'email', 'company', 'phone',
            'name', 'address1', 'address2', 'city', 'state', 'zip', 'country'].includes(key)) {
          return;
        }

        if (key.endsWith('_comment')) {
          const questionId = key.replace('_comment', '');
          if (!submissionData.responses[questionId]) {
            submissionData.responses[questionId] = {};
          }
          submissionData.responses[questionId].comment = value as string;
          return;
        }

        // Handle file upload
        if (typeof value === 'object' && value !== null && 'fileKey' in value) {
          submissionData.responses[key] = { fileKey: value.fileKey };
        }
        // Handle tag questions
        else if (typeof value === 'object' && value !== null && ('tagIds' in value || 'tagRequests' in value || 'questionResponseTaggings' in value)) {
          // Tag question - send the data directly, not wrapped in value
          const tagValue = value as any;
          if (tagValue.tagIds && tagValue.tagIds.length > 0) {
            submissionData.responses[key] = { tagIds: tagValue.tagIds };
          }
          if (tagValue.tagRequests && tagValue.tagRequests.length > 0) {
            submissionData.responses[key] = {
              ...submissionData.responses[key],
              tagRequests: tagValue.tagRequests
            };
          }
          if (tagValue.questionResponseTaggings && tagValue.questionResponseTaggings.length > 0) {
            submissionData.responses[key] = {
              ...submissionData.responses[key],
              questionResponseTaggingsAttributes: tagValue.questionResponseTaggings
            };
          }
        } else {
          const question = getQuestionById(key);
          if (question) {
            // Handle multi-answer questions (checkboxes, multi-select dropdowns, multi-select)
            if ((question.answerStyle === 'checkboxes' || question.answerStyle === 'checkbox' ||
                 (question.answerStyle === 'dropdown' && question.allowMultipleAnswers) ||
                 (question.answerStyle === 'select' && question.allowMultipleAnswers)) &&
                Array.isArray(value)) {
              submissionData.responses[key] = { answerIds: value };
            }
            // Handle single-answer questions (radio, single-select dropdown, select)
            else if ((question.answerStyle === 'dropdown' || question.answerStyle === 'select' || question.answerStyle === 'radio' || question.answerStyle === 'multiple_choice') &&
                     !question.allowMultipleAnswers && typeof value === 'string' && value) {
              submissionData.responses[key] = { answerId: value };
            }
            // Handle all other question types (text, textarea, etc.)
            else {
              submissionData.responses[key] = { value };
            }
          } else {
            // If we can't find the question, check if it's an array (likely multi-select)
            if (Array.isArray(value)) {
              submissionData.responses[key] = { answerIds: value };
            } else {
              submissionData.responses[key] = { value };
            }
          }
        }
      });

      submissionData.authToken = draft.authToken;

      const result = await submitMutation.mutateAsync({
        eventSlug,
        formSlug: form.slug,
        responses: submissionData,
      });

      clearDraft();

      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        showConfirmation: true,
        confirmationData: result,
      }));

      if (result.redirectUrl) {
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 2000);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);

      let errorMessage = '';
      let validationErrors: Record<string, string> = {};

      // Handle StructuredApiError
      if (error?.name === 'StructuredApiError' && error.errors) {
        // Check for base error first
        if (error.errors.base && Array.isArray(error.errors.base) && error.errors.base.length > 0) {
          errorMessage = error.errors.base[0];
        } else {
          // Convert field errors to validation errors
          Object.entries(error.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages.length > 0) {
              validationErrors[field] = messages[0];
            }
          });
        }
      }
      // Handle generic error response with error field
      else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      // Handle error message
      else if (error?.message) {
        errorMessage = error.message;
      }
      // Fallback error message
      else {
        errorMessage = 'An error occurred submitting the form. Please try again.';
      }

      // Set submission error for banner display
      if (errorMessage && Object.keys(validationErrors).length === 0) {
        setSubmissionError(errorMessage);
      }

      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        validationErrors
      }));

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [form, formState.formData, submitMutation, getQuestionById, draft, saveNow, clearDraft]);

  const handleBackToForms = () => {
    if (hasStartedForm && Object.keys(formState.formData).length > 0 && !formState.showConfirmation) {
      setShowExitConfirmation(true);
    } else {
      navigate(`/forms`);
    }
  };

  const confirmExit = () => {
    if (form?.slug || form_slug) {
      const draftKey = `event_form_draft_${eventSlug}_${form?.slug || form_slug}`;
      localStorage.removeItem(draftKey);
      queryClient.removeQueries({ queryKey: ['eventFormDraft', eventSlug, form?.slug || form_slug] });
    }
    navigate(`/forms`);
  };

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    if (!hasStartedForm) {
      setHasStartedForm(true);
      setShowWelcomePage(false);
    }
  };

  // Loading state
  // Only show loading if form is loading or draft is being created initially
  if (isFormLoading || (isDraftLoading && !draft)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">
              {isFormLoading ? 'Loading form...' : 'Preparing your session...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (formError || !form) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Form Not Found</h2>
          <p className="text-gray-600 mb-4">
            The form you're looking for doesn't exist or is no longer available.
          </p>
          <button
            onClick={handleBackToForms}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  // Check form availability
  if (!form.availability.isAvailable) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Form Not Available</h2>
          <p className="text-gray-600 mb-4">
            {form.status === 'upcoming' && 'This form is not yet open for submissions.'}
            {form.status === 'closed' && 'This form is no longer accepting submissions.'}
            {form.status === 'disabled' && 'This form has been disabled.'}
          </p>
          <button
            onClick={handleBackToForms}
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  // Use primary color from form style if available
  const primaryColor = form.buttonColor || '#3B82F6';

  const formActions = {
    updateField,
    markFieldTouched,
    setVisibleChildQuestions,
    nextPage,
    previousPage,
    submitForm,
  };

  // Show confirmation screen
  if (formState.showConfirmation) {
    return (
      <FormConfirmation
        message={formState.confirmationMessage || 'Thank you for your submission!'}
        confirmationData={formState.confirmationData}
        primaryColor={primaryColor}
        onBackToHome={() => navigate(`/details`)}
        onStartOver={() => {
          const draftKey = `event_form_draft_${eventSlug}_${form.slug || form_slug}`;
          localStorage.removeItem(draftKey);
          queryClient.removeQueries({ queryKey: ['eventFormDraft', eventSlug, form.slug || form_slug] });

          setFormState({
            currentPageIndex: 0,
            formData: {},
            touchedFields: new Set(),
            visibleChildQuestions: new Set(),
            validationErrors: {},
            isSubmitting: false,
            showConfirmation: false,
            confirmationData: undefined,
          });
          setShowWelcomePage(true);
          setHasStartedForm(false);
          setDraftDataLoaded(false);
        }}
        onEditSubmission={() => {
          if (formState.confirmationData?.editUrl) {
            navigate(formState.confirmationData.editUrl);
          }
        }}
      />
    );
  }

  // Show welcome page if not skipped and not started
  if (showWelcomePage && !hasStartedForm) {
    return (
      <EventFormWelcome
        form={form}
        primaryColor={primaryColor}
        onStart={() => {
          if (requiresAuth && !isAuthenticated) {
            setShowWelcomePage(false);
            return;
          }

          const draftKey = `event_form_draft_${eventSlug}_${form.slug || form_slug}`;
          localStorage.removeItem(draftKey);

          setBuiltinFieldsCompleted(false);
          setHasStartedForm(true);
          setShowWelcomePage(false);
        }}
      />
    );
  }

  // Show authentication required if needed
  if (requiresAuth && !isAuthenticated && !showWelcomePage) {
    return (
      <div className="flex items-center justify-center p-4 min-h-[400px]">
        <div className="max-w-2xl w-full">
          <AuthenticationRequired
            context="form"
            entityName={form.name}
            primaryColor={primaryColor}
            onAuthenticated={handleAuthenticated}
          />
        </div>
      </div>
    );
  }

  // Show built-in fields step if needed
  const shouldShowBuiltinFields = () => {
    if (!form) return false;
    if (form.allowsAnonymous || form.options?.allow_anonymous) return false;
    return true;
  };

  if (hasStartedForm && !showWelcomePage && !formState.showConfirmation && shouldShowBuiltinFields() && !builtinFieldsCompleted) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <FormBuiltinFields
            form={form}
            formData={formState.formData}
            onUpdateField={updateField}
            onContinue={() => {
              setBuiltinFieldsCompleted(true);
            }}
            onBack={() => {
              setShowWelcomePage(true);
              setHasStartedForm(false);
            }}
            primaryColor={primaryColor}
            isSaving={isSaving}
          />
        </div>
      </div>
    );
  }

  return (
    <FormContext.Provider value={{
      state: formState,
      actions: formActions,
      formData: {
        teamSlug: form.team?.subdomain
      }
    }}>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto mb-32">
          {/* Submission Error Banner */}
          {submissionError && (
            <FormErrorBanner
              error={submissionError}
              onDismiss={() => setSubmissionError(null)}
              className="mb-6"
            />
          )}

          {/* Header */}
          <div className="mb-8">
            <button
              onClick={handleBackToForms}
              className="flex items-center text-gray-600 hover:text-gray-900 bg-white px-3 py-1.5 rounded-lg shadow-sm mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Forms
            </button>

            {/* Form header */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-start mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{form.name}</h1>
                {draft && (
                  <AutosaveIndicator
                    isSaving={isSaving}
                    lastSaved={lastSaved}
                    error={saveError}
                  />
                )}
              </div>
              {form.description && (
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: form.description }}
                />
              )}

              {/* Progress Bar */}
              {pageData?.survey?.totalPages && pageData.survey.totalPages > 1 && (
                <FormProgressBar
                  current={formState.currentPageIndex}
                  total={pageData.survey.totalPages}
                  primaryColor={primaryColor}
                />
              )}
            </div>
          </div>

          {/* Validation Errors Summary */}
          {Object.keys(formState.validationErrors).length > 0 && (
            <div id="validation-errors-summary" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Please correct the following errors:</h3>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    {Object.entries(formState.validationErrors).map(([fieldId, error]) => (
                      <li key={fieldId}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Form Content */}
          {isPageLoading ? (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading page...</p>
            </div>
          ) : pageData?.page ? (
            <>
              <FormPage
                page={pageData.page}
                primaryColor={primaryColor}
              />
              <FormNavigation
                currentPage={formState.currentPageIndex}
                totalPages={pageData.survey?.totalPages || 1}
                isLastPage={formState.currentPageIndex === (pageData.survey?.totalPages || 1) - 1}
                isSubmitting={formState.isSubmitting}
                primaryColor={primaryColor}
              />
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <p className="text-gray-600">No questions available for this form.</p>
            </div>
          )}
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      {showExitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Are you sure you want to leave?
            </h3>
            <p className="text-gray-600 mb-6">
              All progress will be lost and you'll need to start over.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowExitConfirmation(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmExit}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Leave Form
              </button>
            </div>
          </div>
        </div>
      )}
    </FormContext.Provider>
  );
};